import { 
    App, 
    DashboardCustomRole,
    DashboardFeature,
    DashboardFeatureAccessByRole,
    DashboardRole,
    DashboardUserVerificationResult,
    CreateDashboardUserRequest,
    HealthcareProviderInfo, 
    Nullable, 
    ReportTemplate,
    Therapist, 
    TherapistsState, 
    TherapySession,
    TherapySessionsState,
    TimelineDocument,
    TimelineDocumentsState,
    User, 
} from '../interfaces';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
    PresentedData, 
    PresentedView, 
    Presenter, 
} from './Presentation';
import type { TeamMemberTimelineData } from './TeamMemberTimelineView';
import {
    canAssignDashboardRole,
    canManageProviderTeam,
    clampDashboardFeatureAccessToPlan,
    CONFIGURABLE_DASHBOARD_FEATURES,
    createCustomDashboardRoleId,
    getAllDashboardRoles,
    getCompleteDashboardFeatureAccess,
    getConfigurableDashboardRoles,
    getDashboardRole,
    hasDashboardFeature,
    isDashboardFeatureAvailableForProvider,
    isRootOrSuperRole,
} from '../DashboardAccess';
import {
    createExistingProviderPremiumCheckout,
    ExistingProviderPremiumCheckoutRequest,
    ExistingProviderPremiumMigrationOfferState,
    ExistingProviderPremiumMigrationPreview,
    getExistingProviderPremiumMigrationOfferState,
    getExistingProviderPremiumMigrationPreview,
    setExistingProviderPremiumMigrationOfferEnabled,
} from '../saas/SaasApi';

export class OrganisationPresenter implements Presenter {
    private static readonly MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
    private static readonly LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
    private app: App;
    private view: Nullable<PresentedView>;
    private user!: User;
    private healthcareProviderId!: string;
    private healthcareProviderInfo: HealthcareProviderInfo = {
        id: '',
        name: '',
        inviteCodes: [],
        dashboardUrl: '',
    };
    private therapists: Array<Therapist> = [];
    private currentTherapistId: string = '';
    private currentTherapist: Nullable<Therapist> = null;
    private therapySessions: Array<TherapySession> = [];
    private timelineDocuments: Array<TimelineDocument> = [];

    constructor(options: { app: App, healthcareProviderId: string }) {
        const { app, healthcareProviderId } = options;
        if (!app) {
            throw new Error('OrganisationPresenter(): missing app parameter');
        }
        this.app = app;
        this.healthcareProviderId = healthcareProviderId;
        this.observeUser();
        this.observeHealthcareProviders();
        this.observeTherapists();
        this.observeCurrentTherapist();
        this.observeTimelineRecords();
    }

    setHealthcareProviderId(healthcareProviderId: string) {
        this.healthcareProviderId = healthcareProviderId;
        console.log('ORGANISATION_PRESENTER: setHealthcareProviderId() healthcareProviderId:', this.healthcareProviderId);
        this.safeUpdatePresentedData();
    }

    private observeUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                console.log('ORGANISATION_PRESENTER: queryUser().observe()', user);
                this.user = user;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeHealthcareProviders() {
        console.log('ORGANISATION_PRESENTER: observeHealthcareProviders()');
        this.app.getData().queryHealthcareProviderInfo().observe({
            changed: (healthcareProviderInfo: HealthcareProviderInfo) => {
                console.log('ORGANISATION_PRESENTER: queryHealthcareProviderInfo().observe()', healthcareProviderInfo);
                this.healthcareProviderInfo = healthcareProviderInfo;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeTherapists() {
        this.app.getData().queryTherapistsInCurrentHealthcareProvider().observe({
            changed: (therapists: TherapistsState) => {
                console.log('ORGANISATION_PRESENTER: queryTherapists().observe()', therapists);
                this.therapists = therapists.therapists;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeCurrentTherapist() {
        this.app.getData().queryCurrentTherapist().observe({
            changed: (therapist: Therapist) => {
                this.currentTherapistId = therapist.id || '';
                this.currentTherapist = therapist.id ? therapist : null;
                this.safeUpdatePresentedData();
            }
        });
    }

    // Reuse provider-scoped records so one shared recording appears for every selected member.
    private observeTimelineRecords() {
        this.app.getData().queryTherapySessions().observe({
            changed: (state: TherapySessionsState) => {
                this.therapySessions = state.sessions;
                this.safeUpdatePresentedData();
            },
        });
        this.app.getData().queryTimelineDocuments().observe({
            changed: (state: TimelineDocumentsState) => {
                this.timelineDocuments = state.documents;
                this.safeUpdatePresentedData();
            },
        });
    }

    // Recheck the captured provider and current permissions before any member-timeline action.
    private teamTimelineAction<Args extends unknown[], Result>(
        providerId: string,
        feature: 'clients' | 'audio_scribe_reports',
        action: (...args: Args) => Promise<Result>
    ): (...args: Args) => Promise<Result> {
        return async (...args: Args) => {
            if (providerId !== this.healthcareProviderId || !this.canUseFeature('organisation_team') ||
                !this.canUseFeature('clients') || !this.canUseFeature(feature)) {
                throw new Error('Your role cannot access this Team member timeline.');
            }
            return action(...args);
        };
    }

    // Supply the shared timeline view with the same editing operations as Client timelines.
    private getTeamTimelineData(): TeamMemberTimelineData {
        const data = this.app.getData();
        const providerId = this.healthcareProviderId;
        const canUseTimelineDocuments = this.canUseFeature('organisation_team') && this.canUseFeature('clients');
        const canUseAudioScribeAndReports = canUseTimelineDocuments && this.canUseFeature('audio_scribe_reports');
        return {
            currentTherapist: this.currentTherapist,
            therapists: this.therapists,
            sessions: canUseAudioScribeAndReports ? this.therapySessions : [],
            documents: canUseTimelineDocuments ? this.timelineDocuments : [],
            canUseTimelineDocuments,
            canUseAudioScribeAndReports,
            onSaveSessionTitle: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id, title) => data.updateTherapySessionTitle(id, title)),
            onSaveSessionDate: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id, date) => data.updateTherapySessionDate(id, date)),
            onSaveSessionClinicalNotes: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id, notes) => data.updateTherapySessionClinicalNotes(id, notes)),
            onSaveSessionTranscriptUtterances: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id, utterances) => data.updateTherapySessionTranscriptUtterances(id, utterances)),
            onSaveSessionPlacedTags: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id, tags, previousTags) => data.updateTherapySessionPlacedTags(id, tags, previousTags)),
            onSaveTherapistSessionTags: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id, tags) => data.updateTherapistSessionTags(id, tags)),
            onEnsureSessionWaveform: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id) => data.ensureTherapySessionWaveform(id)),
            onRetrySessionTranscription: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (id) => data.retryTherapySessionTranscription(id)),
            onDeleteSession: this.teamTimelineAction(providerId, 'audio_scribe_reports',
                (session) => data.deleteTherapySession(session)),
            onCreateTimelineClinicalNote: this.teamTimelineAction(providerId, 'clients',
                (document) => data.addTimelineClinicalNote(document)),
            onSaveTimelineClinicalNote: this.teamTimelineAction(providerId, 'clients',
                (id, notes) => data.updateTimelineClinicalNote(id, notes)),
            onSaveTimelineDocumentTitle: this.teamTimelineAction(providerId, 'clients',
                (id, title) => data.updateTimelineDocumentTitle(id, title)),
            onSaveTimelineDocumentDate: this.teamTimelineAction(providerId, 'clients',
                (id, date) => data.updateTimelineDocumentDate(id, date)),
            onUploadTimelineFile: this.teamTimelineAction(providerId, 'clients',
                (document, file, options) => data.addTimelineFileDocument(document, file, options)),
            onGetTimelineFileDownloadUrl: this.teamTimelineAction(providerId, 'clients',
                (document) => data.getTimelineFileDownloadUrl(document)),
            onDownloadTimelineFile: this.teamTimelineAction(providerId, 'clients',
                (document) => data.downloadTimelineFileDocument(document)),
            onDeleteTimelineDocument: this.teamTimelineAction(providerId, 'clients',
                (document) => data.deleteTimelineDocument(document)),
        };
    }

    private getDashboardRole(): DashboardRole {
        return this.user?.dashboardRole || 'unauthenticated';
    }

    private canUseFeature(feature: DashboardFeature): boolean {
        return !!this.healthcareProviderId &&
            this.healthcareProviderInfo.id === this.healthcareProviderId &&
            hasDashboardFeature(this.healthcareProviderInfo, this.getDashboardRole(), feature);
    }

    private getDashboardAccess(): DashboardFeatureAccessByRole {
        const roles = getConfigurableDashboardRoles(this.healthcareProviderInfo.dashboardCustomRoles || [])
            .map((role) => role.id);
        const access = getCompleteDashboardFeatureAccess(
            this.healthcareProviderInfo.dashboardFeatureAccess,
            roles
        );
        if (this.healthcareProviderInfo.commercialModel !== 'self_service') {
            return access;
        }

        return clampDashboardFeatureAccessToPlan(
            access,
            this.healthcareProviderInfo.allowedDashboardFeatures || []
        );
    }

    private async createDashboardUser(request: CreateDashboardUserRequest): Promise<void> {
        // The callable repeats provider, Team-feature, and role-hierarchy checks.
        const actorRole = this.getDashboardRole();
        if (!this.canUseFeature('organisation_team_manage') ||
            !canManageProviderTeam(this.healthcareProviderInfo, actorRole)) {
            throw new Error('Your role cannot create dashboard users for this organisation.');
        }
        if (!canAssignDashboardRole(actorRole, request.role)) {
            throw new Error('Your role cannot assign the selected role.');
        }
        await this.app.getData().createDashboardUser(request);
    }

    private async resendDashboardUserVerificationEmail(
        therapistId: string
    ): Promise<DashboardUserVerificationResult> {
        // The callable repeats provider scope, hierarchy, sign-in-method, and cooldown checks.
        const targetTherapist = this.therapists.find((therapist) => therapist.id === therapistId);
        if (!targetTherapist || targetTherapist.signInMethod !== 'email_password' ||
            !this.canUseFeature('organisation_team_manage')) {
            throw new Error('Your role cannot resend verification for this dashboard user.');
        }
        return this.app.getData().resendDashboardUserVerificationEmail(therapistId);
    }

    private async updateTherapist(therapistId: string, name: string, role: DashboardRole): Promise<void> {
        // The server repeats these checks before applying the team change.
        const actorRole = this.getDashboardRole();
        if (!this.canUseFeature('organisation_team_manage') ||
            !canManageProviderTeam(this.healthcareProviderInfo, actorRole)) {
            throw new Error('Your role cannot edit this organisation team.');
        }
        if (!canAssignDashboardRole(actorRole, role)) {
            throw new Error('Your role cannot assign the selected role.');
        }
        await this.app.getData().updateTherapistProfile(therapistId, { name, role });
    }

    private async setMessagingReplyTo(healthcareProviderId: string, therapistId: string): Promise<void> {
        // Recheck old row callbacks against the current provider and Team-management grant.
        const therapist = this.therapists.find((candidate) => candidate.id === therapistId);
        if (healthcareProviderId !== this.healthcareProviderId ||
            !this.canUseFeature('organisation_team_manage') ||
            !canManageProviderTeam(this.healthcareProviderInfo, this.getDashboardRole()) ||
            !therapist || therapist.healthcareProviderId !== healthcareProviderId || !therapist.email?.trim()) {
            throw new Error('Your role cannot set this organisation\'s Messaging reply-to contact.');
        }
        await this.app.getData().setHealthcareProviderMessagingReplyTo(healthcareProviderId, therapistId);
    }

    private async deleteTherapist(therapistId: string): Promise<void> {
        // Deletion uses the same management entitlement and target-role hierarchy as editing.
        const actorRole = this.getDashboardRole();
        const therapist = this.therapists.find((candidate) => candidate.id === therapistId);
        if (!therapist || !this.canUseFeature('organisation_team_manage') ||
            !canManageProviderTeam(this.healthcareProviderInfo, actorRole) ||
            !canAssignDashboardRole(actorRole, getDashboardRole(therapist))) {
            throw new Error('Your role cannot delete this dashboard user.');
        }
        await this.app.getData().deleteDashboardUser(therapistId);
    }

    private readFileAsBase64(file: File): Promise<string> {
        // FileReader avoids converting a multi-megabyte byte array through the JavaScript argument stack.
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error('The logo file could not be read.'));
            reader.onload = () => {
                const dataUrl = typeof reader.result === 'string' ? reader.result : '';
                const separatorIndex = dataUrl.indexOf(',');
                if (separatorIndex < 0) {
                    reject(new Error('The logo file could not be read.'));
                    return;
                }
                resolve(dataUrl.slice(separatorIndex + 1));
            };
            reader.readAsDataURL(file);
        });
    }

    private async updateOrganisationProfile(name: string, file: File | null, removeLogo: boolean): Promise<void> {
        // Capture provider scope before asynchronous file reading and check it again before invoking the server.
        const healthcareProviderId = this.healthcareProviderId;
        if (!this.canUseFeature('organisation_team_manage') ||
            this.healthcareProviderInfo.id !== healthcareProviderId) {
            throw new Error('Your role cannot edit this organisation profile.');
        }

        const trimmedName = name.trim();
        if (!trimmedName || trimmedName.length > 200) {
            throw new Error('Enter an organisation name of 200 characters or fewer.');
        }
        if (file && removeLogo) {
            throw new Error('Choose either a replacement logo or remove the current logo.');
        }
        if (file && !OrganisationPresenter.LOGO_CONTENT_TYPES.includes(file.type)) {
            throw new Error('Choose a PNG, JPEG, or WebP logo.');
        }
        if (file && file.size > OrganisationPresenter.MAX_LOGO_SIZE_BYTES) {
            throw new Error('Choose a logo no larger than 2 MB.');
        }

        const logo = file ? {
            base64: await this.readFileAsBase64(file),
            contentType: file.type,
            fileName: file.name,
        } : undefined;
        if (this.healthcareProviderId !== healthcareProviderId ||
            !this.canUseFeature('organisation_team_manage')) {
            throw new Error('The selected organisation changed before the profile could be saved.');
        }

        // Omit absent optional fields entirely; callable transports may normalize undefined to null.
        const updates = logo ? { name: trimmedName, logo } :
            removeLogo ? { name: trimmedName, removeLogo: true } : { name: trimmedName };
        await this.app.getData().updateHealthcareProviderProfile(healthcareProviderId, updates);
    }

    // Capture the displayed provider so retained controls cannot modify another organisation.
    private async setDashboardFeature(
        healthcareProviderId: string,
        role: DashboardRole,
        feature: DashboardFeature,
        enabled: boolean
    ): Promise<void> {
        const isSelfServiceAdmin = this.healthcareProviderInfo.commercialModel === 'self_service' &&
            this.getDashboardRole() === 'admin';
        if (!isRootOrSuperRole(this.getDashboardRole()) && !isSelfServiceAdmin) {
            throw new Error('Only an organisation Admin or platform administrator can configure dashboard access.');
        }
        if (!healthcareProviderId || healthcareProviderId !== this.healthcareProviderId ||
            this.healthcareProviderInfo.id !== healthcareProviderId) {
            throw new Error('Wait for the selected healthcare provider to finish loading.');
        }
        if (!CONFIGURABLE_DASHBOARD_FEATURES.some((item) => item.id === feature) ||
            !isDashboardFeatureAvailableForProvider(this.healthcareProviderInfo, feature) ||
            !getConfigurableDashboardRoles(this.healthcareProviderInfo.dashboardCustomRoles || [])
                .some((item) => item.id === role)) {
            throw new Error('This role or feature is not available for this organisation.');
        }

        const dashboardFeatureAccess = this.getDashboardAccess();
        dashboardFeatureAccess[role] = {
            ...(dashboardFeatureAccess[role] || {}),
            [feature]: enabled,
        };
        // Keep the two Team permissions coherent: management includes viewing.
        if (feature === 'organisation_team_manage' && enabled) {
            dashboardFeatureAccess[role].organisation_team = true;
        } else if (feature === 'organisation_team' && !enabled) {
            dashboardFeatureAccess[role].organisation_team_manage = false;
        }
        await this.app.getData().updateHealthcareProviderDashboardAccess(
            healthcareProviderId,
            dashboardFeatureAccess,
            this.healthcareProviderInfo.dashboardCustomRoles || []
        );
        await auth.currentUser?.getIdToken(true);
    }

    // Availability is a platform setting and leaves every role's saved permissions intact.
    private async setDashboardFeatureAvailability(
        healthcareProviderId: string,
        feature: DashboardFeature,
        enabled: boolean
    ): Promise<void> {
        if (!isRootOrSuperRole(this.getDashboardRole())) {
            throw new Error('Only Root or Super users can configure feature availability.');
        }
        if (!healthcareProviderId || healthcareProviderId !== this.healthcareProviderId ||
            this.healthcareProviderInfo.id !== healthcareProviderId) {
            throw new Error('Wait for the selected healthcare provider to finish loading.');
        }
        if (!CONFIGURABLE_DASHBOARD_FEATURES.some((item) => item.id === feature)) {
            throw new Error('This feature cannot be configured for an organisation.');
        }
        await this.app.getData().updateHealthcareProviderFeatureAvailability(healthcareProviderId, feature, enabled);
    }

    private async openBillingPortal(): Promise<void> {
        const createPortal = httpsCallable<
            { healthcareProviderId: string },
            { url: string }
        >(getFunctions(), 'createBillingPortalSession');
        const result = await createPortal({
            healthcareProviderId: this.healthcareProviderId,
        });
        if (!result.data.url) {
            throw new Error('The billing portal could not be opened.');
        }
        window.location.assign(result.data.url);
    }

    // Increase Premium capacity through the server-owned Stripe subscription item.
    private async changeSubscriptionUnits(subscriptionUnits: number): Promise<void> {
        const changeUnits = httpsCallable<
            { healthcareProviderId: string; subscriptionUnits: number },
            { subscriptionUnits: number; updated: boolean }
        >(getFunctions(), 'changePremiumSubscriptionUnits');
        await changeUnits({
            healthcareProviderId: this.healthcareProviderId,
            subscriptionUnits,
        });
    }

    private async transferBillingOwnership(newOwnerTherapistId: string): Promise<void> {
        const transferOwnership = httpsCallable<
            { healthcareProviderId: string; newOwnerTherapistId: string },
            { healthcareProviderId: string; newOwnerTherapistId: string; transferred: boolean }
        >(getFunctions(), 'transferProviderBillingOwnership');
        const result = await transferOwnership({
            healthcareProviderId: this.healthcareProviderId,
            newOwnerTherapistId,
        });
        if (result.data.healthcareProviderId !== this.healthcareProviderId ||
            result.data.newOwnerTherapistId !== newOwnerTherapistId) {
            throw new Error('Billing ownership was returned for the wrong organisation.');
        }

        // Reflect the completed transfer immediately; the next server directory refresh remains authoritative.
        this.therapists = this.therapists.map((therapist) => ({
            ...therapist,
            billingRole: therapist.id === newOwnerTherapistId ? 'owner' :
                therapist.billingRole === 'owner' ? 'none' : therapist.billingRole,
        }));
        this.healthcareProviderInfo = {
            ...this.healthcareProviderInfo,
            entitlement: this.healthcareProviderInfo.entitlement ? {
                ...this.healthcareProviderInfo.entitlement,
                billingOwnerUserId: newOwnerTherapistId,
            } : undefined,
        };
        this.safeUpdatePresentedData();
    }

    private async getPremiumMigrationPreview(): Promise<ExistingProviderPremiumMigrationPreview> {
        // Root validates any legacy provider; an Admin can only read a server-approved current offer for their own.
        const healthcareProviderId = this.healthcareProviderId;
        if (!['root', 'admin'].includes(this.getDashboardRole()) ||
            this.healthcareProviderInfo.id !== healthcareProviderId ||
            this.healthcareProviderInfo.commercialModel === 'self_service') {
            throw new Error('This account cannot view a Premium migration offer for this organisation.');
        }
        const preview = await getExistingProviderPremiumMigrationPreview(healthcareProviderId);
        if (this.healthcareProviderId !== healthcareProviderId) {
            throw new Error('The selected organisation changed while migration requirements were loading.');
        }
        return preview;
    }

    private async getPremiumMigrationOfferState(): Promise<ExistingProviderPremiumMigrationOfferState> {
        const healthcareProviderId = this.healthcareProviderId;
        if (this.getDashboardRole() !== 'root' ||
            this.healthcareProviderInfo.id !== healthcareProviderId ||
            this.healthcareProviderInfo.commercialModel === 'self_service') {
            throw new Error('Only Root can manage a Premium migration offer.');
        }
        return getExistingProviderPremiumMigrationOfferState(healthcareProviderId);
    }

    private async setPremiumMigrationOfferEnabled(
        enabled: boolean,
        validationToken?: string
    ): Promise<ExistingProviderPremiumMigrationOfferState> {
        const healthcareProviderId = this.healthcareProviderId;
        if (this.getDashboardRole() !== 'root' ||
            this.healthcareProviderInfo.id !== healthcareProviderId ||
            this.healthcareProviderInfo.commercialModel === 'self_service') {
            throw new Error('Only Root can manage a Premium migration offer.');
        }
        const offerState = await setExistingProviderPremiumMigrationOfferEnabled(
            healthcareProviderId,
            enabled,
            validationToken
        );
        if (this.healthcareProviderId !== healthcareProviderId) {
            throw new Error('The selected organisation changed while the customer offer was being updated.');
        }
        return offerState;
    }

    private async createPremiumMigrationCheckout(
        request: ExistingProviderPremiumCheckoutRequest
    ): Promise<void> {
        // The signed-in Admin becomes billing owner; Root can validate and expose the offer but cannot pay for it.
        const healthcareProviderId = this.healthcareProviderId;
        if (this.getDashboardRole() !== 'admin' ||
            this.healthcareProviderInfo.id !== healthcareProviderId ||
            this.healthcareProviderInfo.commercialModel === 'self_service') {
            throw new Error('An active Admin must complete this organisation’s Premium Checkout.');
        }
        if (!this.currentTherapistId || request.billingOwnerTherapistId !== this.currentTherapistId) {
            throw new Error('The signed-in Admin must own the Premium subscription.');
        }
        const result = await createExistingProviderPremiumCheckout(healthcareProviderId, request);
        if (this.healthcareProviderId !== healthcareProviderId) {
            throw new Error('The selected organisation changed before Stripe Checkout could be opened.');
        }
        window.location.assign(result.checkoutUrl);
    }

    private async createCustomRole(name: string): Promise<void> {
        if (!isRootOrSuperRole(this.getDashboardRole())) {
            throw new Error('Only Root or Super users can create dashboard roles.');
        }
        if (!this.healthcareProviderId || this.healthcareProviderInfo.id !== this.healthcareProviderId) {
            throw new Error('Wait for the selected healthcare provider to finish loading.');
        }

        const trimmedName = name.trim();
        const baseId = createCustomDashboardRoleId(trimmedName);
        if (!baseId) {
            throw new Error('Enter a custom role name.');
        }

        const existingRoleIds = new Set(getAllDashboardRoles(this.healthcareProviderInfo.dashboardCustomRoles || [])
            .map((role) => role.id));
        let customRoleId = baseId;
        let suffix = 2;
        while (existingRoleIds.has(customRoleId)) {
            customRoleId = `${baseId}_${suffix}`;
            suffix += 1;
        }

        const dashboardCustomRoles: Array<DashboardCustomRole> = [
            ...(this.healthcareProviderInfo.dashboardCustomRoles || []),
            { id: customRoleId, name: trimmedName },
        ];
        const dashboardFeatureAccess = getCompleteDashboardFeatureAccess(
            this.healthcareProviderInfo.dashboardFeatureAccess,
            getConfigurableDashboardRoles(dashboardCustomRoles).map((role) => role.id)
        );
        await this.app.getData().updateHealthcareProviderDashboardAccess(
            this.healthcareProviderId,
            dashboardFeatureAccess,
            dashboardCustomRoles
        );
        await auth.currentUser?.getIdToken(true);
    }

    private async deleteCustomRole(role: DashboardRole): Promise<void> {
        if (!isRootOrSuperRole(this.getDashboardRole())) {
            throw new Error('Only Root or Super users can delete dashboard roles.');
        }
        if (!this.healthcareProviderId || this.healthcareProviderInfo.id !== this.healthcareProviderId) {
            throw new Error('Wait for the selected healthcare provider to finish loading.');
        }
        if (this.therapists.some((therapist) => getDashboardRole(therapist) === role)) {
            throw new Error('Reassign every team member using this role before deleting it.');
        }

        const dashboardCustomRoles = (this.healthcareProviderInfo.dashboardCustomRoles || [])
            .filter((customRole) => customRole.id !== role);
        const dashboardFeatureAccess = { ...this.getDashboardAccess() };
        delete dashboardFeatureAccess[role];
        await this.app.getData().updateHealthcareProviderDashboardAccess(
            this.healthcareProviderId,
            dashboardFeatureAccess,
            dashboardCustomRoles
        );
        await auth.currentUser?.getIdToken(true);
    }

    // Keep uploaded template object names storage-safe while preserving the original display name.
    private sanitizeTemplateFileName(fileName: string): string {
        return fileName
            .replace(/[^a-zA-Z0-9._-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '') || 'template';
    }

    private createReportTemplateId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    private getSortedReportTemplates(reportTemplates: Array<ReportTemplate>): Array<ReportTemplate> {
        return [...reportTemplates].sort((leftTemplate, rightTemplate) =>
            leftTemplate.name.localeCompare(rightTemplate.name)
        );
    }

    private async createReportTemplate(name: string, file: File | null, promptExtraInstructions: string): Promise<void> {
        // Store template metadata first, adding Storage file details only when a document is provided.
        if (!this.canUseFeature('audio_scribe_reports')) {
            throw new Error('Your role does not have access to report templates.');
        }
        const trimmedName = name.trim();
        const trimmedPromptExtraInstructions = promptExtraInstructions.trim();
        if (!this.healthcareProviderId) {
            throw new Error('Select a healthcare provider before creating a report template.');
        }

        if (!trimmedName) {
            throw new Error('A report template name is required.');
        }

        const templateId = this.createReportTemplateId();
        let reportTemplate: ReportTemplate = {
            id: templateId,
            name: trimmedName,
            promptExtraInstructions: trimmedPromptExtraInstructions,
            uploadedAt: new Date().toISOString(),
            uploadedByUserId: this.user?.authenticatedUser?.uid,
            uploadedByEmail: this.user?.authenticatedUser?.email || null,
        };

        if (file) {
            const safeFileName = this.sanitizeTemplateFileName(file.name);
            const storagePath = `HealthcareProviders/${this.healthcareProviderId}/ReportTemplates/${templateId}-${safeFileName}`;
            await uploadBytes(ref(storage, storagePath), file, {
                contentType: file.type || 'application/octet-stream',
            });

            reportTemplate = {
                ...reportTemplate,
                originalName: file.name,
                storagePath,
                contentType: file.type || 'application/octet-stream',
                sizeBytes: file.size,
            };
        }

        const nextReportTemplates = this.getSortedReportTemplates([
            ...(this.healthcareProviderInfo.reportTemplates || []),
            reportTemplate,
        ]);

        await this.app.getData().updateHealthcareProviderReportTemplates(this.healthcareProviderId, nextReportTemplates);
    }

    private async updateReportTemplate(
        reportTemplate: ReportTemplate,
        name: string,
        file: File | null,
        promptExtraInstructions: string
    ): Promise<void> {
        // Update metadata in place, optionally replacing the uploaded template document.
        if (!this.canUseFeature('audio_scribe_reports')) {
            throw new Error('Your role does not have access to report templates.');
        }
        const trimmedName = name.trim();
        const trimmedPromptExtraInstructions = promptExtraInstructions.trim();
        if (!this.healthcareProviderId) {
            throw new Error('Select a healthcare provider before editing a report template.');
        }

        if (!trimmedName) {
            throw new Error('A report template name is required.');
        }

        const existingReportTemplate = (this.healthcareProviderInfo.reportTemplates || [])
            .find((template) => template.id === reportTemplate.id);
        if (!existingReportTemplate) {
            throw new Error('This report template no longer exists.');
        }

        let updatedReportTemplate: ReportTemplate = {
            ...existingReportTemplate,
            name: trimmedName,
            promptExtraInstructions: trimmedPromptExtraInstructions,
        };
        const previousStoragePath = existingReportTemplate.storagePath;
        if (file) {
            const safeFileName = this.sanitizeTemplateFileName(file.name);
            const storagePath = `HealthcareProviders/${this.healthcareProviderId}/ReportTemplates/${existingReportTemplate.id}-${safeFileName}`;
            await uploadBytes(ref(storage, storagePath), file, {
                contentType: file.type || 'application/octet-stream',
            });

            updatedReportTemplate = {
                ...updatedReportTemplate,
                originalName: file.name,
                storagePath,
                contentType: file.type || 'application/octet-stream',
                sizeBytes: file.size,
                uploadedAt: new Date().toISOString(),
                uploadedByUserId: this.user?.authenticatedUser?.uid,
                uploadedByEmail: this.user?.authenticatedUser?.email || null,
            };
        }

        const nextReportTemplates = this.getSortedReportTemplates(
            (this.healthcareProviderInfo.reportTemplates || [])
                .map((template) => template.id === updatedReportTemplate.id ? updatedReportTemplate : template)
        );
        await this.app.getData().updateHealthcareProviderReportTemplates(this.healthcareProviderId, nextReportTemplates);

        if (file && previousStoragePath && previousStoragePath !== updatedReportTemplate.storagePath) {
            try {
                await deleteObject(ref(storage, previousStoragePath));
            } catch (error) {
                if ((error as { code?: string })?.code !== 'storage/object-not-found') {
                    console.warn('ORGANISATION_PRESENTER: updateReportTemplate(): Could not delete replaced template file:', error);
                }
            }
        }
    }

    private async deleteReportTemplate(reportTemplate: ReportTemplate): Promise<void> {
        // Remove both the Storage file and the provider metadata row.
        if (!this.canUseFeature('audio_scribe_reports')) {
            throw new Error('Your role does not have access to report templates.');
        }
        if (reportTemplate.storagePath) {
            try {
                await deleteObject(ref(storage, reportTemplate.storagePath));
            } catch (error) {
                if ((error as { code?: string })?.code !== 'storage/object-not-found') {
                    throw error;
                }
            }
        }

        const nextReportTemplates = (this.healthcareProviderInfo.reportTemplates || [])
            .filter((template) => template.id !== reportTemplate.id);
        await this.app.getData().updateHealthcareProviderReportTemplates(this.healthcareProviderId, nextReportTemplates);
    }

    private async downloadReportTemplate(reportTemplate: ReportTemplate): Promise<void> {
        // Use a temporary anchor so browsers download when allowed and open otherwise.
        if (!this.canUseFeature('audio_scribe_reports')) {
            throw new Error('Your role does not have access to report templates.');
        }
        if (!reportTemplate.storagePath) {
            throw new Error('This report template does not have a stored file.');
        }

        const downloadUrl = await getDownloadURL(ref(storage, reportTemplate.storagePath));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = reportTemplate.originalName || `${reportTemplate.name}.docx`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    presentedData(): PresentedData {
        const healthcareProviderId = this.healthcareProviderId;
        const dashboardRole = this.getDashboardRole();
        const dashboardCustomRoles = this.healthcareProviderInfo.dashboardCustomRoles || [];
        return {
            organisationName: this.healthcareProviderInfo.name,
            organisationLogoUrl: this.healthcareProviderInfo.logoUrl,
            messagingReplyToTherapistId: this.healthcareProviderInfo.messagingReplyToTherapistId,
            dashboardUrl: this.healthcareProviderInfo.dashboardUrl,
            heatmapUrl: this.healthcareProviderInfo.heatmapUrl,
            userId: this.user?.authenticatedUser?.uid || '',
            healthcareProviderId: this.healthcareProviderId,
            therapists: this.therapists,
            currentTherapistId: this.currentTherapistId,
            teamTimeline: this.getTeamTimelineData(),
            reportTemplates: this.getSortedReportTemplates(this.healthcareProviderInfo.reportTemplates || []),
            canViewAnalyticsAndHeatmap: this.canUseFeature('organisation_analytics_heatmap'),
            canViewTeam: this.canUseFeature('organisation_team'),
            canViewReportTemplates: this.canUseFeature('audio_scribe_reports'),
            canManageTeam: this.canUseFeature('organisation_team_manage') &&
                canManageProviderTeam(this.healthcareProviderInfo, dashboardRole),
            canManageOrganisationProfile: this.canUseFeature('organisation_team_manage'),
            commercialModel: this.healthcareProviderInfo.commercialModel,
            planCode: this.healthcareProviderInfo.selfServicePlanCode,
            entitlement: this.healthcareProviderInfo.entitlement,
            licenseUsage: this.healthcareProviderInfo.licenseUsage,
            allowedDashboardFeatures: this.healthcareProviderInfo.allowedDashboardFeatures || [],
            canManageBilling: this.healthcareProviderInfo.commercialModel === 'self_service' &&
                (this.healthcareProviderInfo.entitlement?.billingOwnerUserId === this.user?.authenticatedUser?.uid ||
                    isRootOrSuperRole(dashboardRole)),
            dashboardRole,
            dashboardRoles: getConfigurableDashboardRoles(dashboardCustomRoles),
            assignableDashboardRoles: getAllDashboardRoles(dashboardCustomRoles)
                .filter((role) => canAssignDashboardRole(dashboardRole, role.id)),
            dashboardFeatures: CONFIGURABLE_DASHBOARD_FEATURES.filter((feature) =>
                isDashboardFeatureAvailableForProvider(this.healthcareProviderInfo, feature.id)),
            dashboardFeatureAccess: this.getDashboardAccess(),
            dashboardFeatureAvailability: this.healthcareProviderInfo.dashboardFeatureAvailability,
            canConfigureDashboardFeatureAvailability: isRootOrSuperRole(dashboardRole) &&
                this.healthcareProviderInfo.id === healthcareProviderId,
            dashboardCustomRoles,
            canConfigureDashboardAccess: (isRootOrSuperRole(dashboardRole) ||
                (this.healthcareProviderInfo.commercialModel === 'self_service' && dashboardRole === 'admin')) &&
                this.healthcareProviderInfo.id === this.healthcareProviderId,
            canCreateCustomRoles: isRootOrSuperRole(dashboardRole) &&
                this.healthcareProviderInfo.commercialModel !== 'self_service',
            onUpdateTherapist: async (therapistId: string, name: string, role: DashboardRole) =>
                this.updateTherapist(therapistId, name, role),
            onDeleteTherapist: async (therapistId: string) => this.deleteTherapist(therapistId),
            onSetMessagingReplyTo: async (therapistId: string) =>
                this.setMessagingReplyTo(healthcareProviderId, therapistId),
            onUpdateOrganisationProfile: async (name: string, file: File | null, removeLogo: boolean) =>
                this.updateOrganisationProfile(name, file, removeLogo),
            onCreateDashboardUser: async (request: CreateDashboardUserRequest) =>
                this.createDashboardUser(request),
            onResendDashboardUserVerificationEmail: async (therapistId: string) =>
                this.resendDashboardUserVerificationEmail(therapistId),
            onSetDashboardFeature: async (role: DashboardRole, feature: DashboardFeature, enabled: boolean) =>
                this.setDashboardFeature(healthcareProviderId, role, feature, enabled),
            onSetDashboardFeatureAvailability: async (feature: DashboardFeature, enabled: boolean) =>
                this.setDashboardFeatureAvailability(healthcareProviderId, feature, enabled),
            onCreateCustomRole: async (name: string) => this.createCustomRole(name),
            onDeleteCustomRole: async (role: DashboardRole) => this.deleteCustomRole(role),
            onOpenBillingPortal: async () => this.openBillingPortal(),
            onChangeSubscriptionUnits: async (subscriptionUnits: number) =>
                this.changeSubscriptionUnits(subscriptionUnits),
            onTransferBillingOwnership: async (newOwnerTherapistId: string) =>
                this.transferBillingOwnership(newOwnerTherapistId),
            onGetExistingProviderPremiumMigrationPreview: async () =>
                this.getPremiumMigrationPreview(),
            onGetExistingProviderPremiumMigrationOfferState: async () =>
                this.getPremiumMigrationOfferState(),
            onSetExistingProviderPremiumMigrationOfferEnabled: async (
                enabled: boolean,
                validationToken?: string
            ) => this.setPremiumMigrationOfferEnabled(enabled, validationToken),
            onCreateExistingProviderPremiumCheckout: async (request: ExistingProviderPremiumCheckoutRequest) =>
                this.createPremiumMigrationCheckout(request),
            onCreateReportTemplate: async (
                name: string,
                file: File | null,
                promptExtraInstructions: string
            ) => this.createReportTemplate(name, file, promptExtraInstructions),
            onUpdateReportTemplate: async (
                reportTemplate: ReportTemplate,
                name: string,
                file: File | null,
                promptExtraInstructions: string
            ) => this.updateReportTemplate(reportTemplate, name, file, promptExtraInstructions),
            onDeleteReportTemplate: async (reportTemplate: ReportTemplate) => this.deleteReportTemplate(reportTemplate),
            onDownloadReportTemplate: async (reportTemplate: ReportTemplate) => this.downloadReportTemplate(reportTemplate),
        };
    }

    viewWillUpdate(view: PresentedView): void {
        this.view = view;
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    private safeUpdatePresentedData() {
        const data = this.presentedData();
        if (this.view) {
            this.view.updatePresentedData(data);
        }
    }
}
